<script setup lang="ts">
import { ref, onMounted, watch, computed } from 'vue'
import { Lock, Eye, Search, Loader2, PackageOpen } from 'lucide-vue-next'
import { debounce } from 'lodash'
import axios from 'axios'

interface TeacherContent {
  id: number
  title: string
  slug: string
  description: string
  thumbnail_url: string
  formatted_duration: string
  view_count: number
  subject: {
    id: number
    name: string
    slug: string
    color: string
  }
}

interface Subject {
  id: number
  name: string
  slug: string
  color: string
}

interface Props {
  tutorSlug: string
}

const props = defineProps<Props>()

const contents = ref<TeacherContent[]>([])
const subjects = ref<Subject[]>([])
const selectedSubjectId = ref<number | null>(null)
const searchQuery = ref('')
const loading = ref(false)
const initialLoading = ref(true)
const loadingMore = ref(false)
const currentPage = ref(1)
const lastPage = ref(1)
const total = ref(0)

const hasMore = computed(() => currentPage.value < lastPage.value)
const isEmpty = computed(() => !loading.value && !initialLoading.value && contents.value.length === 0)

const fetchContent = async (page: number = 1, append: boolean = false) => {
  if (append) {
    loadingMore.value = true
  } else if (page === 1) {
    initialLoading.value = true
  } else {
    loading.value = true
  }

  try {
    const params: any = { page }

    if (selectedSubjectId.value) {
      params.subject_id = selectedSubjectId.value
    }

    if (searchQuery.value.trim()) {
      params.search = searchQuery.value.trim()
    }

    const response = await axios.get(`/tutors/${props.tutorSlug}/content`, { params })

    if (append) {
      contents.value = [...contents.value, ...response.data.data]
    } else {
      contents.value = response.data.data
    }

    currentPage.value = response.data.meta.current_page
    lastPage.value = response.data.meta.last_page
    total.value = response.data.meta.total
  } catch (error) {
    console.error('Failed to fetch content:', error)
  } finally {
    loading.value = false
    initialLoading.value = false
    loadingMore.value = false
  }
}

const fetchSubjects = async () => {
  try {
    const response = await axios.get(`/tutors/${props.tutorSlug}/content/subjects`)
    subjects.value = response.data.data
  } catch (error) {
    console.error('Failed to fetch subjects:', error)
  }
}

const loadMore = () => {
  if (!hasMore.value || loadingMore.value) return
  fetchContent(currentPage.value + 1, true)
}

const handleSubjectChange = () => {
  currentPage.value = 1
  fetchContent(1, false)
}

const debouncedSearch = debounce(() => {
  currentPage.value = 1
  fetchContent(1, false)
}, 500)

watch(searchQuery, () => {
  debouncedSearch()
})

watch(selectedSubjectId, handleSubjectChange)

onMounted(() => {
  fetchSubjects()
  fetchContent()
})

const getContentUrl = (content: TeacherContent) => {
  return `/library/${content.slug}`
}
</script>

<template>
  <div>
    <div class="flex flex-col md:flex-row items-center justify-between mb-4">
      <h3 class="text-xl font-semibold text-gray-900">Premium Content Library</h3>
      <div class="flex items-center gap-3 mt-4 md:mt-0">
        <!-- Subject Filter -->
        <select
          v-model="selectedSubjectId"
          class="px-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700"
        >
          <option :value="null">All Subjects</option>
          <option
            v-for="subject in subjects"
            :key="subject.id"
            :value="subject.id"
          >
            {{ subject.name }}
          </option>
        </select>

        <!-- Search Input -->
        <div class="relative">
          <Search class="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search content..."
            class="pl-10 pr-4 py-2 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-600 w-64"
          >
        </div>
      </div>
    </div>

    <!-- Loading Skeleton -->
    <div v-if="initialLoading" class="grid grid-cols-3 gap-4">
      <div
        v-for="i in 6"
        :key="i"
        class="relative bg-gray-200 rounded-xl overflow-hidden animate-pulse"
      >
        <div class="aspect-video bg-gray-300" />
        <div class="p-4 space-y-2">
          <div class="h-4 bg-gray-300 rounded w-3/4" />
          <div class="h-3 bg-gray-300 rounded w-1/2" />
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <PackageOpen class="w-16 h-16 text-gray-400 mb-4" />
      <h3 class="text-lg font-semibold text-gray-900 mb-2">No Content Found</h3>
      <p class="text-gray-600 max-w-md">
        <template v-if="searchQuery || selectedSubjectId">
          No content matches your search criteria. Try adjusting your filters.
        </template>
        <template v-else>
          This tutor hasn't published any content yet. Check back soon!
        </template>
      </p>
    </div>

    <!-- Content Grid -->
    <div v-else class="grid grid-cols-3 gap-4">
      <a
        v-for="content in contents"
        :key="content.id"
        :href="getContentUrl(content)"
        class="relative bg-gray-900 rounded-xl overflow-hidden group cursor-pointer hover:ring-2 hover:ring-blue-600 transition-all"
      >
        <div class="aspect-video relative">
          <img
            v-if="content.thumbnail_url"
            :src="content.thumbnail_url"
            :alt="content.title"
            class="w-full h-full object-cover"
          >
          <div v-else class="w-full h-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center">
            <div class="text-center">
              <div class="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock class="w-8 h-8 text-white" />
              </div>
              <p class="text-white font-medium">Premium Content</p>
            </div>
          </div>
        </div>
        <div class="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
        <div class="absolute bottom-0 left-0 right-0 p-4">
          <p class="text-white font-medium mb-1 line-clamp-2">{{ content.title }}</p>
          <div class="flex items-center justify-between text-sm text-white/80">
            <span>{{ content.formatted_duration }}</span>
            <span class="flex items-center gap-1">
              <Eye class="w-4 h-4" />
              {{ content.view_count.toLocaleString() }}
            </span>
          </div>
        </div>
      </a>
    </div>

    <!-- Load More Button -->
    <div v-if="hasMore && !initialLoading" class="mt-6 text-center">
      <button
        @click="loadMore"
        :disabled="loadingMore"
        class="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
      >
        <Loader2 v-if="loadingMore" class="w-5 h-5 animate-spin" />
        <span>{{ loadingMore ? 'Loading...' : 'Load More' }}</span>
      </button>
    </div>
  </div>
</template>
